import { useEffect, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useFulfiller } from '../FulfillerContext';
import { useSnackbar } from '../SnackbarContext';
import {
  BreadcrumbItem,
  Breadcrumbs,
  Button,
  NavTab,
  NavTabItem,
  Spinner,
} from '@cimpress/react-components';
import { LandingPage } from './LandingPage';
import GeneralTab from '../components/GeneralTab';
import AttributesTab from '../components/AttributesTab';
import StagesTab from '../components/StagesTab';
import RelationshipsTab from '../components/RelationshipsTab';
import styles from '../css/pages.module.scss';

import { CreateEntityTypeRequest } from '@/models/EntityType';
import {
  getEntityType,
  patchEntityType,
  putEntityType,
  putEntityTypeRelationships,
} from '../services/entity.management.service';

import {
  convertFromEntityTypeAttributes,
  convertToEntityTypeAttributes,
} from '@/utilities/common';

import { CARDINALITY_OPTIONS, CreateEntityTypeTab } from '../constants';
import type {
  GeneralTabFormData,
  Attribute,
  Stage,
  Relationship,
  EntityTypeAttributes,
} from '../models/EditableList';

import {
  normalizeEntityTypeForCompare,
  validateEntityTypeForm,
} from '@/services/common.service';
import isEqual from 'lodash/isEqual';
import { useSelector } from 'react-redux';
import { RootState } from '@/store/store';
import NotFoundState from '../components/common/NotFoundState';

const EditEntityTypePage = () => {
  const { entityTypeId } = useParams();
  const [searchParams] = useSearchParams();
  const tenant = searchParams.get('tenantId');
  const navigate = useNavigate();
  const { fulfillerData } = useFulfiller();
  const { showSnackbar } = useSnackbar();
  const [originalEntityType, setOriginalEntityType] =
    useState<CreateEntityTypeRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState<CreateEntityTypeTab>(
    CreateEntityTypeTab.GENERAL,
  );
  const [formData, setFormData] = useState<GeneralTabFormData>({
    entityTypeName: '',
    tags: [],
    description: '',
  });

  const [attributes, setAttributes] = useState<Attribute[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [relationships, setRelationships] = useState<Relationship[]>([]);
  const [originalAttributeIds, setOriginalAttributeIds] = useState<string[]>(
    [],
  );
  const [originalStageIds, setOriginalStageIds] = useState<string[]>([]);
  const [originalRelationshipIds, setOriginalRelationshipIds] = useState<
    string[]
  >([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [version, setVersion] = useState<string | undefined>();
  const tenantId = fulfillerData?.fulfillerLocation.value;
  const [isNotFound, setIsNotFound] = useState(false);

  const permission = useSelector((state: RootState) => state.permission);
  useEffect(() => {
    if (!permission.update) {
      navigate(`/entityTypes`);
    }
  }, [permission, navigate, fulfillerData]);

  useEffect(() => {
    if (!tenantId || !entityTypeId || !tenant || tenantId !== tenant) {
      setIsNotFound(true);
      setLoading(false);
      return;
    }

    let isActive = true;
    setLoading(true);
    setIsNotFound(false);

    getEntityType(tenantId, entityTypeId)
      .then((data) => {
        if (!isActive || tenantId !== tenant) {
          return;
        }
        if (!data) {
          setIsNotFound(true);
          setLoading(false);
          return;
        }
        // Pre-fill general
        setVersion(data?.versionId);
        const normalized = normalizeEntityTypeForCompare(data);
        setOriginalEntityType(normalized);
        setFormData({
          entityTypeName: data.entityTypeName,
          tags: data.tags ?? [],
          description: data.description ?? '',
        });

        if (data.entityTypeAttributes?.properties) {
          // Keep original keys for API submission
          const converted = convertFromEntityTypeAttributes(
            data.entityTypeAttributes,
            false, // Don't transform names
          );
          setAttributes(converted);
          // Collect all original attribute IDs including nested children
          const collectAllIds = (attrs: Attribute[]): string[] => {
            return attrs.flatMap((attr) => [
              attr.id,
              ...(attr.children ? collectAllIds(attr.children) : []),
            ]);
          };
          setOriginalAttributeIds(collectAllIds(converted));
        }

        if (data.statusMapping) {
          const mappedStages = data.statusMapping.map((sm) => ({
            id: sm.stage,
            stage: sm.stage,
            status: sm.status,
          }));
          setStages(mappedStages);
          setOriginalStageIds(mappedStages.map((s) => s.id));
        }

        if (data.entityTypeRelationships) {
          const mappedRelationships = data.entityTypeRelationships.map(
            (rel) => ({
              id: rel.relatedEntityTypeId,
              entityTypeId: rel.relatedEntityTypeId,
              cardinality: CARDINALITY_OPTIONS[3].label,
            }),
          );
          setRelationships(mappedRelationships);
          setOriginalRelationshipIds(mappedRelationships.map((r) => r.id));
        }

        setLoading(false);
      })
      .catch(() => {
        if (!isActive) {
          return;
        }
        setIsNotFound(true);
        setLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [entityTypeId, tenant, tenantId]);

  const getEntityTypeAttributes = (): EntityTypeAttributes | null => {
    return convertToEntityTypeAttributes(attributes);
  };

  /**
   * Sync handler for attribute changes from UI.
   * Original attributes are read-only, so we preserve them with original keys.
   * New attributes are added as-is (user-entered names).
   */
  const handleAttributeChange = (updatedDisplayAttributes: Attribute[]) => {
    setAttributes(updatedDisplayAttributes);
  };

  const handleSubmit = async () => {
    const tenantId = fulfillerData?.fulfillerLocation.value;

    if (!tenantId) {
      showSnackbar('Tenant ID is not available', 'danger');
      return;
    }
    if (!entityTypeId || !version || !originalEntityType) return;

    const validation = validateEntityTypeForm(formData, stages, relationships);

    if (!validation.isValid) {
      showSnackbar(validation.error!, 'danger');
      return;
    }

    const payload: CreateEntityTypeRequest = {
      ...formData,
      tags: formData.tags.length > 0 ? formData.tags : [],
      entityTypeAttributes: getEntityTypeAttributes() ?? {
        additionalProperties: false,
        type: 'object',
        properties: {},
      },
      statusMapping:
        stages.filter((stage) => stage.stage?.trim()).length > 0
          ? stages
              .filter((stage) => stage.stage?.trim())
              .map((stage) => ({
                stage: stage.stage,
                status: stage.status,
              }))
          : [],
      entityTypeRelationships:
        relationships.length > 0
          ? relationships.map((rel) => ({
              relatedEntityTypeId: rel.entityTypeId,
            }))
          : [],
    };
    const noChange = isEqual(originalEntityType, payload);
    if (noChange) {
      showSnackbar('No change Detected', 'warning');
      return;
    }

    const breakingChange =
      payload.entityTypeName !== originalEntityType.entityTypeName ||
      !isEqual(payload.statusMapping, originalEntityType.statusMapping) ||
      !isEqual(
        payload.entityTypeAttributes,
        originalEntityType.entityTypeAttributes,
      );

    const onlyMetaUnchanged =
      isEqual(payload.tags, originalEntityType.tags) &&
      payload.description === originalEntityType.description;

    const updateRelations = !isEqual(
      payload.entityTypeRelationships,
      originalEntityType.entityTypeRelationships,
    );
    try {
      setIsSubmitting(true);

      // STEP 1 — Update relationships first
      if (updateRelations) {
        try {
          await putEntityTypeRelationships(
            tenantId,
            entityTypeId,
            payload.entityTypeRelationships,
          );
          // eslint-disable-next-line @typescript-eslint/no-unused-vars
        } catch (error) {
          showSnackbar('Failed to update entity type', 'danger');
          return;
        }
      }

      try {
        // STEP 2 — Update entity type
        if (breakingChange) {
          await putEntityType(tenantId, entityTypeId, payload);
        } else if (!onlyMetaUnchanged) {
          await patchEntityType(tenantId, entityTypeId, payload, version);
        }

        showSnackbar(
          breakingChange
            ? 'New version created successfully'
            : 'Entity Type updated successfully',
          'success',
        );

        navigate(`/entitytypes/${entityTypeId}?tenantId=${tenantId}`);
      } catch (step2Error) {
        // 🔁 ROLLBACK STEP 1 if Step 2 fails
        if (updateRelations) {
          try {
            await putEntityTypeRelationships(
              tenantId,
              entityTypeId,
              originalEntityType.entityTypeRelationships,
            );
          } catch (rollbackError) {
            console.error('Rollback failed:', rollbackError);
          }
        }

        throw step2Error; // rethrow so outer catch handles snackbar
      }
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      showSnackbar('Failed to update entity type', 'danger');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!fulfillerData) return <LandingPage />;
  if (loading) return <Spinner />;
  if (isNotFound) {
    const notFoundLink = tenantId ? `/entitytypes?tenantId=${tenantId}` : '/';
    return (
      <NotFoundState
        message={`Unable to find entity type with ID ${entityTypeId} at tenant ${tenant}.`}
        navigationLink={{ to: notFoundLink, text: 'Entity Types' }}
      />
    );
  }

  let tabContent;
  switch (selectedTab) {
    case CreateEntityTypeTab.GENERAL:
      tabContent = <GeneralTab formData={formData} onChange={setFormData} />;
      break;
    case CreateEntityTypeTab.ATTRIBUTES:
      tabContent = (
        <AttributesTab
          attributes={attributes}
          onChange={handleAttributeChange}
          readOnlyIds={originalAttributeIds}
        />
      );
      break;
    case CreateEntityTypeTab.STAGES:
      tabContent = (
        <StagesTab
          stages={stages}
          onChange={setStages}
          readOnlyIds={originalStageIds}
        />
      );
      break;
    case CreateEntityTypeTab.RELATIONSHIPS:
      tabContent = (
        <RelationshipsTab
          relationships={relationships}
          onChange={setRelationships}
          readOnlyIds={originalRelationshipIds}
        />
      );
      break;
  }

  return (
    <div className={styles.entityTypePage}>
      <Breadcrumbs className="breadcrumbs">
        <BreadcrumbItem className="breadcrumb-item">
          <Button variant="anchor" onClick={() => navigate(`/entitytypes`)}>
            <div className="breadcrumb-item-text-link">Entity Types</div>
          </Button>
        </BreadcrumbItem>
        <BreadcrumbItem className="breadcrumb-item">
          <div className="breadcrumb-item-text">Edit Entity Type</div>
        </BreadcrumbItem>
      </Breadcrumbs>

      <div className={styles.entityTypeTopRow}>
        <div className={styles.entityTypeNameContainer}>
          <h1>Edit Entity Type</h1>
        </div>
        <div>
          <Button
            className={styles.entityTypeDeleteButton}
            variant="secondary"
            onClick={() =>
              navigate(`/entitytypes/${entityTypeId}?tenantId=${tenantId}`)
            }
          >
            Cancel
          </Button>

          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
      <div className={styles.entityTypeNavTabContainer}>
        <NavTab className={styles.entityTypeNavTabContainer}>
          <NavTabItem active={selectedTab === CreateEntityTypeTab.GENERAL}>
            <button onClick={() => setSelectedTab(CreateEntityTypeTab.GENERAL)}>
              General
            </button>
          </NavTabItem>
          <NavTabItem active={selectedTab === CreateEntityTypeTab.ATTRIBUTES}>
            <button
              onClick={() => setSelectedTab(CreateEntityTypeTab.ATTRIBUTES)}
            >
              Attributes
            </button>
          </NavTabItem>
          <NavTabItem active={selectedTab === CreateEntityTypeTab.STAGES}>
            <button onClick={() => setSelectedTab(CreateEntityTypeTab.STAGES)}>
              Stages
            </button>
          </NavTabItem>
          <NavTabItem
            active={selectedTab === CreateEntityTypeTab.RELATIONSHIPS}
          >
            <button
              onClick={() => setSelectedTab(CreateEntityTypeTab.RELATIONSHIPS)}
            >
              Relationships
            </button>
          </NavTabItem>
        </NavTab>

        {tabContent}
      </div>
    </div>
  );
};

export default EditEntityTypePage;
